import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Close as CloseIcon } from "@material-ui/icons";
import useFirestore from "../../../hooks/useFirestore";
import { PATH_DASHBOARD } from "../../../routes/paths";
import { preloadSignatureSendData, getDocument } from "../../../services/signatures";
import type { SignatureDocument } from "../../../@types/signatures";
import NewSignatureForm, {
  SignatureQuickPickRole,
  SIGNATURE_QUICK_PICK_ROLES
} from "./NewSignatureForm";
import PreparedSignatureDialog from "./PreparedSignatureDialog";
import type { PreparedSignatureResult } from "./PreparedSignatureDialog";

export const NEW_SIGNATURE_PARAM = "newSignature";
export const NEW_SIGNATURE_CONTACT_PARAM = "newSignatureContact";
export const NEW_SIGNATURE_TEMPLATE_PARAM = "newSignatureTemplate";
export const NEW_SIGNATURE_BUNDLE_PARAM = "newSignatureBundle";
export const NEW_SIGNATURE_ROLE_PARAM = "newSignatureRole";
// "1" / "0": the opening state of the role's exclusivity checkbox (seller /
// landlord). Absent leaves the role's own default. Set by the WhatsApp AI's
// prepare_signature_document deep link (server: signatureAi.service
// buildNewSignatureUrl) — keep the name in sync.
export const NEW_SIGNATURE_EXCLUSIVE_PARAM = "newSignatureExclusive";
export const NEW_SIGNATURE_EDIT_DOCUMENT_PARAM = "newSignatureEditDocument";
// "1": open a seller / landlord role with "שאלון בלבד" ticked — the questionnaire-only document
// instead of the role's order (the property card's "שליחת שאלון לבעל הנכס").
export const NEW_SIGNATURE_QUESTIONNAIRE_ONLY_PARAM = "newSignatureQuestionnaireOnly";
// Comma-separated listing ids to pre-select in the send form's listings
// picker. Set by the WhatsApp AI's prepare_signature_document deep link
// (server: signatureAi.service buildNewSignatureUrl) — keep the name in sync.
export const NEW_SIGNATURE_LISTINGS_PARAM = "newSignatureListings";

/**
 * Build a URL search string that opens the "new signature" dialog on top of
 * the page it's applied to. Callers use this with `setSearchParams` (or by
 * constructing a URL) so the dialog is openable from anywhere — contact row
 * menus, templates list, FAB — without having to navigate off the current
 * page.
 *
 * When both `bundleId` and `templateId` are passed, bundleId wins — the form
 * goes into bundle mode and ignores the single-template selection. `role`
 * pre-selects a signing scenario in the quick-pick row (buyer/seller/renter/
 * landlord) so the matching default template is auto-picked; an explicit
 * template/bundle wins over it. `listingIds` (yad2-listings-latest doc ids)
 * pre-selects those listings in the form's listing picker once the picked
 * template actually consumes listings — used by the WhatsApp AI deep link and
 * by seller-side entry points (e.g. the customer page) to prefill the
 * contact's own listing.
 */
export function buildNewSignatureParams(opts?: {
  contactId?: string;
  templateId?: string;
  bundleId?: string;
  role?: SignatureQuickPickRole;
  /**
   * Opens the form in EDIT mode for this SENT (unsigned) document. The dialog
   * loads the document and prefills the form from it; submit goes through the
   * edit-resend endpoint silently, followed by the explicit sharing options.
   * Mutually exclusive with template/bundle/role — edit derives them itself.
   */
  editDocumentId?: string;
  /** Listing ids to pre-select in the send form's listings picker. */
  listingIds?: string[];
  /** With a seller / landlord `role`: tick "שאלון בלבד" (the questionnaire alone, no order). */
  questionnaireOnly?: boolean;
}): Record<string, string> {
  const out: Record<string, string> = { [NEW_SIGNATURE_PARAM]: "1" };
  if (opts?.contactId) out[NEW_SIGNATURE_CONTACT_PARAM] = opts.contactId;
  if (opts?.templateId) out[NEW_SIGNATURE_TEMPLATE_PARAM] = opts.templateId;
  if (opts?.bundleId) out[NEW_SIGNATURE_BUNDLE_PARAM] = opts.bundleId;
  if (opts?.role) out[NEW_SIGNATURE_ROLE_PARAM] = opts.role;
  if (opts?.editDocumentId) out[NEW_SIGNATURE_EDIT_DOCUMENT_PARAM] = opts.editDocumentId;
  if (opts?.listingIds?.length) out[NEW_SIGNATURE_LISTINGS_PARAM] = opts.listingIds.join(",");
  if (opts?.questionnaireOnly) out[NEW_SIGNATURE_QUESTIONNAIRE_ONLY_PARAM] = "1";
  return out;
}

/**
 * Globally-mounted dialog that hosts <NewSignatureForm/>. It listens to URL
 * search params so any part of the app can open the flow by appending
 * `?newSignature=1&newSignatureContact=<id>&newSignatureTemplate=<id>` to the
 * current URL. Closing the dialog simply strips those params, leaving the
 * user on whatever page they started from.
 */
export default function GlobalNewSignatureDialog(): JSX.Element | null {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { userSettings } = useFirestore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [preparing, setPreparing] = useState(false);
  const [prepared, setPrepared] = useState<PreparedSignatureResult | null>(null);

  const hasSignatureFeatureFlag =
    userSettings?.featureFlagsV2?.includes("signature") ?? false;

  // Warm the send-form picker data (templates + bundles) as soon as we know
  // the user has the feature — the dialog then opens instantly instead of
  // blocking on a /templates round-trip. The form still revalidates on open.
  useEffect(() => {
    if (hasSignatureFeatureFlag) preloadSignatureSendData();
  }, [hasSignatureFeatureFlag]);
  const open = searchParams.get(NEW_SIGNATURE_PARAM) === "1";
  const contactId = searchParams.get(NEW_SIGNATURE_CONTACT_PARAM);
  const templateId = searchParams.get(NEW_SIGNATURE_TEMPLATE_PARAM);
  const bundleId = searchParams.get(NEW_SIGNATURE_BUNDLE_PARAM);
  const roleParam = searchParams.get(NEW_SIGNATURE_ROLE_PARAM);
  const exclusiveParam = searchParams.get(NEW_SIGNATURE_EXCLUSIVE_PARAM);
  const editDocumentId = searchParams.get(NEW_SIGNATURE_EDIT_DOCUMENT_PARAM);
  const listingsParam = searchParams.get(NEW_SIGNATURE_LISTINGS_PARAM);
  const questionnaireOnlyParam = searchParams.get(NEW_SIGNATURE_QUESTIONNAIRE_ONLY_PARAM);
  const initialListingIds = useMemo(
    () => (listingsParam ? listingsParam.split(",").map((id) => id.trim()).filter(Boolean) : null),
    [listingsParam]
  );
  const role =
    roleParam && SIGNATURE_QUICK_PICK_ROLES.includes(roleParam as SignatureQuickPickRole)
      ? (roleParam as SignatureQuickPickRole)
      : null;

  // EDIT MODE: load the document being edited, then render the form prefilled
  // from it (template + contact ride the standard initial* props). Loaded
  // fresh on every open so the eligibility state isn't stale.
  const [editDocument, setEditDocument] = useState<SignatureDocument | null>(null);
  const [editDocumentLoading, setEditDocumentLoading] = useState(false);
  const [editDocumentError, setEditDocumentError] = useState<string | null>(null);
  useEffect(() => {
    if (!open || !editDocumentId) {
      setEditDocument(null);
      setEditDocumentLoading(false);
      setEditDocumentError(null);
      return undefined;
    }
    let cancelled = false;
    setEditDocumentLoading(true);
    setEditDocumentError(null);
    getDocument(editDocumentId)
      .then((doc) => {
        if (!cancelled) setEditDocument(doc);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load document for edit", err);
        if (!cancelled) {
          setEditDocumentError(
            err?.response?.data?.error || err?.message || (t("signatures.detail.loadError") as string)
          );
        }
      })
      .finally(() => {
        if (!cancelled) setEditDocumentLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editDocumentId]);

  const close = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(NEW_SIGNATURE_PARAM);
    next.delete(NEW_SIGNATURE_CONTACT_PARAM);
    next.delete(NEW_SIGNATURE_TEMPLATE_PARAM);
    next.delete(NEW_SIGNATURE_BUNDLE_PARAM);
    next.delete(NEW_SIGNATURE_ROLE_PARAM);
    next.delete(NEW_SIGNATURE_EXCLUSIVE_PARAM);
    next.delete(NEW_SIGNATURE_EDIT_DOCUMENT_PARAM);
    next.delete(NEW_SIGNATURE_LISTINGS_PARAM);
    next.delete(NEW_SIGNATURE_QUESTIONNAIRE_ONLY_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const finish = useCallback(
    (result: PreparedSignatureResult) => {
      // After the sharing options close, jump to the contract list by id.
      // Bundle: filter the sent list by bundleId so the realtor sees the
      // freshly-prepared group together.
      if (result.bundleId) {
        navigate(`${PATH_DASHBOARD.signatures.sent}?bundle=${result.bundleId}`);
      } else if (result.documentId) {
        navigate(`${PATH_DASHBOARD.signatures.sent}?open=${result.documentId}`);
      } else {
        navigate(PATH_DASHBOARD.signatures.sent);
      }
    },
    [navigate]
  );

  // Remount the form when the incoming params change so initial selections
  // re-apply. Using a key is simpler than threading reset handlers into the
  // form itself.
  const formKey = useMemo(
    () =>
      `${open ? "open" : "closed"}:${contactId || ""}:${templateId || ""}:${bundleId || ""}:${role || ""}:${exclusiveParam || ""}:${editDocumentId || ""}:${listingsParam || ""}:${questionnaireOnlyParam || ""}`,
    [open, contactId, templateId, bundleId, role, exclusiveParam, editDocumentId, listingsParam, questionnaireOnlyParam]
  );

  if (!hasSignatureFeatureFlag) return null;

  return (
    <>
    <Dialog
      open={open && !prepared}
      onClose={() => { if (!preparing) close(); }}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          // On mobile use `100dvh` (dynamic viewport height) instead of
          // `100vh` — on iOS Safari the latter is the LAYOUT viewport,
          // which extends behind the address bar, so the dialog ended up
          // taller than the visible screen and the bottom "Send" controls
          // landed off-screen. `dvh` tracks the actual visible area. We
          // also clamp maxHeight so any tall internal content can't push
          // the dialog past the viewport.
          height: isMobile ? "100dvh" : "95vh",
          maxHeight: isMobile ? "100dvh" : "95vh"
        }
      }}
    >
      <DialogTitle sx={{ py: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="h6" component="span">
            {editDocumentId ? t("signatures.newDoc.editTitle") : t("signatures.newDoc.title")}
          </Typography>
          <IconButton onClick={close} disabled={preparing} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        dividers
        // p:0 + overflow:hidden — the form owns its own scroll area and a
        // fixed action footer below it (see NewSignatureForm's root layout).
        // Padding here would push the footer off the dialog edge, and a
        // scrolling DialogContent would let content slide under the footer.
        sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}
      >
        {open && editDocumentId ? (
          editDocumentError ? (
            <Box sx={{ p: 3 }}>
              <Typography color="error">{editDocumentError}</Typography>
            </Box>
          ) : editDocumentLoading || !editDocument ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : (
            <NewSignatureForm
              key={formKey}
              // Template + contact prefill ride the standard initial props;
              // everything else the form reads off editDocument itself.
              initialContactId={editDocument.contactIds[0] || null}
              initialTemplateId={editDocument.templateId}
              initialBundleId={null}
              initialRole={null}
              editDocument={editDocument}
              onSent={setPrepared}
              onBusyChange={setPreparing}
            />
          )
        ) : open ? (
          <NewSignatureForm
            key={formKey}
            initialContactId={contactId}
            initialTemplateId={templateId}
            initialBundleId={bundleId}
            initialRole={role}
            initialExclusivity={
              exclusiveParam === "1" ? true : exclusiveParam === "0" ? false : null
            }
            initialQuestionnaireOnly={questionnaireOnlyParam === "1"}
            initialListingIds={initialListingIds}
            onSent={setPrepared}
            onBusyChange={setPreparing}
          />
        ) : null}
      </DialogContent>
    </Dialog>
    {prepared && <PreparedSignatureDialog result={prepared} onClose={() => {
      finish(prepared);
      setPrepared(null);
    }} />}
    </>
  );
}
